import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EventAttributeFieldKey, FORM_CONFIG } from './event-attribute-form.config';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../global/form/form-field/form-field';
import { eventAttributeTypes } from '../../../../../constants/attribute-values';
import { OEToggle } from '../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../global/text/text';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { APPLY_TEXT, cancelText, LOADING, MORE_BUTTON_TEXT } from '../../../../../constants/common-labels.constants';
import OptionsButton from '../../../../global/options-button/options-button';
import { EventAttributeFormVM } from './event-attribute-form.viewmodel';
import { AttributeValue, EventAttributeTypesValues } from '../../../../../api/attributes.dao';
import { ITheadSort, ListLayout } from '../../../../global/tables/table-list-layout';
import DefaultButton from '../../../../global/default-button/default-button';
import './event-attribute-form.scss';
import { EventAttributeAddValuePresentation } from './event-attribute-add-value-form/event-attribute-add-value-form.presentation';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { MenuIcon } from '../../../../global/icons/icons';
import { editText } from '../../../../../constants/common-labels.constants';

export type EventAttributeFormPresentationProps = {
    vm: EventAttributeFormVM;
};

export const EventAttributeFormPresentation = observer(({ vm }: EventAttributeFormPresentationProps): JSX.Element => {
    const intl = useIntl();
    const overlayRef = useRef<HTMLDivElement>(null);
    const [editOverlayStyle, setEditOverlayStyle] = useState<React.CSSProperties | null>(null);

    const {
        name,
        description,
        externalId,
        isActive,
        inputType,
    } = vm.getFormFields(EventAttributeFieldKey);

    const attributeOptionsButton = [
        {
            label: intl.formatMessage({
                id: `oe.eventAttribute.deleteEventAttribute`,
                description: 'Label text',
                defaultMessage: 'Delete Event Attribute',
            }),
            action: vm.deleteEventAttribute,
            danger: true
        }
    ].filter(Boolean);

    const valueTableHeaders: ITheadSort<keyof AttributeValue>[] = [
        { width: '40px' },
        { title: 'Label', width: '85px' },
        { title: 'Status', width: '45px' },
        { title: 'External ID', width: '85px' },
        { width: '80px' }
    ];

    const onDragEnd = ({ source, destination }: DropResult): void => {
        if (!destination || source.index === destination.index) return;

        if (source.droppableId === destination.droppableId) {
            vm.reorder(source.index, destination.index);
        } else {
            vm.move(source.index, destination.index);
        }
    };

    const showValuesSection = inputType.value === EventAttributeTypesValues['SINGLE_SELECT_DROPDOWN'] || inputType.value === EventAttributeTypesValues['MULTI_SELECT_DROPDOWN'];

    useEffect(() => {
        if (vm.isAddValuePopupOpen && vm.overlayPositionMode === 'edit' && overlayRef.current) {
            const overlayRect = overlayRef.current.getBoundingClientRect();
            const estimatedOverlayHeight = 300;
            const overlayWidth = 400;

            const centerTop = overlayRect.top + (overlayRect.height / 2) - (estimatedOverlayHeight / 2);
            const centerLeft = overlayRect.left + (overlayRect.width / 2) - (overlayWidth / 2);

            setEditOverlayStyle({
                position: 'fixed',
                top: `${Math.max(overlayRect.top + 10, centerTop)}px`,
                left: `${Math.max(overlayRect.left + 10, centerLeft)}px`,
                zIndex: 10000,
            });
        } else {
            setEditOverlayStyle(null);
        }
    }, [vm.isAddValuePopupOpen, vm.overlayPositionMode]);

    return (
        <div className={`event-attribute-form-wrapper ${vm.isAddValuePopupOpen ? 'event-attribute-form-wrapper--popup-open' : ''}`}>
            {vm.isAddValuePopupOpen && <div ref={overlayRef} className="event-attribute-form-overlay" />}
            <CommonPopupBodyFooterLayout
                body={
                    <form className={`oe-form oe-form--event-attribute ${vm.isAddValuePopupOpen ? 'oe-form--popup-open' : ''}`}>
                        {vm.loading ? (
                            <FormattedMessage
                                {...LOADING}
                                values={{
                                    suffix: '...',
                                }}
                            />
                        ) : (
                            <div className='oe-form__body'>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage {...FORM_CONFIG[EventAttributeFieldKey.NAME].labelDescriptor} />}
                                >
                                    <input
                                        className='oe-form-field__control'
                                        value={name.value as string}
                                        type='text'
                                        onChange={e => name.setValue(e.target.value)}
                                        onBlur={name.setTouched} />
                                    <span className='oe-form-field__error'>
                                        {!name.valid && name.touched &&
                                            <FormattedMessage
                                                values={{ fieldName: intl.formatMessage(FORM_CONFIG[EventAttributeFieldKey.NAME].labelDescriptor) }}
                                                {...name.validationMessage} />}
                                    </span>
                                </FormField>
                                <FormField
                                    className='m-bottom-20px'
                                >
                                    <div className='attribute-toggle'>
                                        <OEToggle
                                            isSelected={isActive.value as boolean}
                                            onClick={() => isActive.setValue(!isActive.value)} />
                                        <Text
                                            message='Attribute field is {prefix}'
                                            id='common.createPassportSite'
                                            description='Label text'
                                            values={{
                                                prefix: isActive.value ? 'active' : 'inactive'
                                            }} />
                                    </div>
                                </FormField>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage {...FORM_CONFIG[EventAttributeFieldKey.EXTERNAL_ID].labelDescriptor} />}
                                >
                                    <input
                                        className='oe-form-field__control'
                                        value={externalId.value as string}
                                        type='text'
                                        onChange={e => externalId.setValue(e.target.value)}
                                        onBlur={externalId.setTouched} />
                                    <span className='oe-form-field__error'>
                                        {!externalId.valid && externalId.touched &&
                                            <FormattedMessage
                                                values={{ fieldName: intl.formatMessage(FORM_CONFIG[EventAttributeFieldKey.EXTERNAL_ID].labelDescriptor) }}
                                                {...externalId.validationMessage} />}
                                    </span>
                                </FormField>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage {...FORM_CONFIG[EventAttributeFieldKey.DESCRIPTION].labelDescriptor} />}
                                >
                                    <input
                                        className='oe-form-field__control'
                                        value={description.value as string}
                                        type='text'
                                        onChange={e => description.setValue(e.target.value)}
                                        onBlur={description.setTouched} />
                                    <span className='oe-form-field__error'>
                                        {!description.valid && description.touched &&
                                            <FormattedMessage
                                                values={{ fieldName: intl.formatMessage(FORM_CONFIG[EventAttributeFieldKey.DESCRIPTION].labelDescriptor) }}
                                                {...description.validationMessage} />}
                                    </span>
                                </FormField>
                                <div className='oe-form__row m-bottom-20px'>
                                    <FormField
                                        label={<FormattedMessage {...FORM_CONFIG[EventAttributeFieldKey.INPUT_TYPE].labelDescriptor} />}
                                        className='w-40 m-right-0px'
                                    >
                                        <select
                                            className='oe-form-field__control'
                                            value={inputType.value as string}
                                            onChange={e => {
                                                inputType.setValue(e.target.value);
                                                vm.clearValuesError();
                                            }}
                                            disabled={vm.isEdit()}
                                        >
                                            <option value={''}>
                                                {intl.formatMessage({
                                                    id: `oe.event.selectInputType`,
                                                    description: 'Label text',
                                                    defaultMessage: 'Select Input Type',
                                                })}
                                            </option>
                                            {eventAttributeTypes(intl).map(({ label, value }, i) => {
                                                return <option value={value} key={i}>{label}</option>;
                                            })}
                                        </select>
                                        <span className='oe-form-field__error'>
                                            {!inputType.valid && inputType.touched &&
                                                <FormattedMessage
                                                    values={{ fieldName: intl.formatMessage(FORM_CONFIG[EventAttributeFieldKey.INPUT_TYPE].labelDescriptor) }}
                                                    {...inputType.validationMessage} />}
                                        </span>
                                    </FormField>
                                </div>
                                {showValuesSection && (
                                    <div className='oe-form__column m-bottom-20px'>
                                        <Text message='Values' className='m-bottom-10px' />
                                        <DragDropContext onDragEnd={onDragEnd}>
                                            <Droppable droppableId="table-droppable" direction="vertical">
                                                {droppableProps => (
                                                    <div {...droppableProps.droppableProps} ref={droppableProps.innerRef}>
                                                        <ListLayout sortOptions={{ theads: valueTableHeaders }}>
                                                            {vm.activeValues.map((val, index) => {
                                                                const uniqueId = val.valueId !== undefined
                                                                    ? String(val.valueId)
                                                                    : `temp-${val.attributeOrder}-${val.label}`;
                                                                return (
                                                                    <Draggable
                                                                        key={uniqueId}
                                                                        draggableId={uniqueId}
                                                                        index={index}
                                                                    >
                                                                        {(provided, snapshot) => (
                                                                            <tr
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                {...provided.dragHandleProps}
                                                                                className={snapshot.isDragging ? 'dragging-row' : ''}
                                                                                style={provided.draggableProps.style}
                                                                            >
                                                                                <td><MenuIcon /></td>
                                                                                <td>
                                                                                    <Text message={val.label} withEllipsis />
                                                                                </td>
                                                                                <td>
                                                                                    <Text message='Active' withEllipsis />
                                                                                </td>
                                                                                <td>
                                                                                    <Text message={val.externalId} withEllipsis />
                                                                                </td>
                                                                                <td>
                                                                                    <DefaultButton
                                                                                        modifier='slim'
                                                                                        text={editText()}
                                                                                        onClick={() => vm.openAddValuePopup(intl, val)}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {droppableProps.placeholder}
                                                        </ListLayout>
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                        {vm.inactiveValues.length > 0 && (
                                            <div className="inactive-values-section">
                                                <ListLayout sortOptions={{ theads: valueTableHeaders }}>
                                                    {vm.inactiveValues.map(val => (
                                                        <tr key={val.valueId !== undefined ? String(val.valueId) : `inactive-${val.attributeOrder}-${val.label}`} className="inactive-row">
                                                            <td>
                                                                <Text message={val.label} withEllipsis />
                                                            </td>
                                                            <td>
                                                                <Text message='Inactive' withEllipsis />
                                                            </td>
                                                            <td>
                                                                <Text message={val.externalId} withEllipsis />
                                                            </td>
                                                            <td>
                                                                <DefaultButton
                                                                    modifier='slim'
                                                                    text={editText()}
                                                                    onClick={() => vm.openAddValuePopup(intl, val)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </ListLayout>
                                            </div>
                                        )}
                                        <div className="add-value-wrapper">
                                            <DefaultButton
                                                className='add--button'
                                                modifier='slim'
                                                onClick={() => vm.openAddValuePopup(intl)}
                                                text={<FormattedMessage
                                                    id='oe.attendee.addValue'
                                                    description='Button text'
                                                    defaultMessage='+ Add Value' />}
                                            />
                                            {vm.addValueError && (
                                                <div className="oe-form-field__error m-top-10px">
                                                    {vm.addValueError}
                                                </div>
                                            )}
                                            {vm.valuesError && (
                                                <div className="oe-form-field__error m-top-10px">
                                                    {vm.valuesError}
                                                </div>
                                            )}
                                            {vm.isAddValuePopupOpen && vm.overlayPositionMode === 'add' && <EventAttributeAddValuePresentation vm={vm.addValueVM} />}
                                        </div>
                                    </div>
                                )}
                                {vm.isAddValuePopupOpen && vm.addValueVM.isOpen && vm.overlayPositionMode === 'edit' && (
                                    <EventAttributeAddValuePresentation
                                        vm={vm.addValueVM}
                                        positionMode={vm.overlayPositionMode}
                                        positionStyle={editOverlayStyle}
                                    />
                                )}
                            </div>
                        )}
                    </form>
                }
                footer={
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={(APPLY_TEXT.defaultMessage)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => vm.submitForm(intl)}
                            disabled={vm.loading || vm.isSaving}
                        />
                        <DefaultCloseSubWindowButton text={cancelText()} />
                        {vm.isEdit() && !!attributeOptionsButton.length &&
                            <OptionsButton
                                className='options-button--right-margin-auto'
                                modifier={['fat', 'options']}
                                label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                                options={attributeOptionsButton}
                                position='top'
                                align='start'
                            />
                        }
                    </div>
                }
            />
        </div>
    );
});
